// use-run-eve — verkabelt alle Phase-1-Hooks zu einem fertigen `runEve()`-Call.
//
// Multi-Page-Chat (Doc-Anweisung 2026-05-15): jede Page hat eigene Chat-
// History (pageKey = location.pathname). useRunEve nimmt entweder den expliziten
// pageKey-Override oder den aktuellen Location-Pathname.
//
// V1-Pendant: src/hooks/useRunEve.js (synchron). V3 ist streamed + multi-page.

import { useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useEntityState } from '@/hooks/use-entity-state';
import { useEveMemory } from '@/hooks/use-eve-memory';
import { useEveProfile } from '@/hooks/use-eve-profile';
import { useEvePortfolio } from '@/hooks/use-eve-portfolio';
import { useTradingLog } from '@/hooks/use-trading-log';
import { useMacroHistoryStore, selectMacroEntries } from '@/stores/macro-history-store';
import { selectFullData, useMarketStore } from '@/stores/market-store';
import {
  selectLiveMetrics,
  useHyperliquidStore,
} from '@/stores/hyperliquid-store';
import { createChatMessage, useEveChatStore } from '@/stores/eve-chat-store';
import { rememberImage, recallImage } from '@/stores/eve-last-image-store';
import { selectChatMode, useEveChatModeStore } from '@/stores/eve-chat-mode-store';
import {
  selectCurrentPageContext,
  useEvePageContextStore,
  formatPageContextBlock,
} from '@/stores/eve-page-context-store';
import { deriveMarketStats } from '@/lib/market-cache';
import {
  InsufficientCreditsError,
  runEveStreamed,
  type ChatHistoryTurn,
  type RuntimeContext,
} from '@/services/eve-runtime';
import { selectAccessToken, selectIsOwner, selectProfile, useAuthStore } from '@/stores/auth-store';
import type { AgentKey } from '@/types/eve-profile';

export interface RunEveCall {
  /** Welcher Agent — default 'direct' (User-Composer). */
  agentKey?: AgentKey | 'direct';
  /** User-Eingabe — wird als user-Message in den Chat gepushed. */
  userMsg?: string;
  /** Optional Bild-Base64. */
  imageData?: string;
  /** Optional echter MIME-Typ des Bildes (image/png|jpeg|webp). */
  imageMime?: string;
  /** Unterdrueckt den Gesamtmarkt-Snapshot im System-Prompt (fokussierte Takes). */
  noLiveData?: boolean;
  /** Optional pageKey override — sonst location.pathname. */
  pageKey?: string;
  /** Optional: was im Chat als User-Bubble angezeigt wird. Wenn nicht
   *  gesetzt, zeigt der Chat den vollen `userMsg`. Trennt die kurze
   *  Display-Frage vom langen Research-Prompt (Frag-EVE-Buttons). */
  displayText?: string;
}

export interface UseRunEveReturn {
  send: (call: RunEveCall) => Promise<string | null>;
  loading: boolean;
  error: string | null;
}

export function useRunEve(): UseRunEveReturn {
  const location = useLocation();
  const accessToken = useAuthStore(selectAccessToken);
  const profile = useAuthStore(selectProfile);
  const isOwner = useAuthStore(selectIsOwner);

  const fullData = useMarketStore(selectFullData);
  const liveMetrics = useHyperliquidStore(selectLiveMetrics);
  const macroEntries = useMacroHistoryStore(selectMacroEntries);
  const { memories, rasRules, globalRasRules, systemRisk } = useEveMemory();
  const { profile: eveProfile } = useEveProfile();
  const { portfolio } = useEvePortfolio();
  const { trades } = useTradingLog();
  const {
    entityState,
    emotionalContext,
    stressLevel,
    focusLevel,
    decisionConfidence,
  } = useEntityState({ enableTicks: false });

  const pushMessage = useEveChatStore((s) => s.push);
  const appendToLast = useEveChatStore((s) => s.appendToLast);
  const appendCitation = useEveChatStore((s) => s.appendCitation);
  const finishStreaming = useEveChatStore((s) => s.finishStreaming);
  const markError = useEveChatStore((s) => s.markError);
  const removeLastIfEmptyAssistant = useEveChatStore((s) => s.removeLastIfEmptyAssistant);
  const chatMode = useEveChatModeStore(selectChatMode);
  const pageContext = useEvePageContextStore(selectCurrentPageContext);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const send = useCallback<UseRunEveReturn['send']>(
    async ({ agentKey = 'direct', userMsg, imageData, imageMime, noLiveData, pageKey: pageKeyOverride, displayText }) => {
      const pageKey = pageKeyOverride ?? location.pathname;
      const callId = Math.random().toString(36).slice(2, 8);
      console.info(
        `[EVE/run #${callId}] start | page=${pageKey} | agent=${agentKey} | mode=${chatMode} | msg=${(userMsg ?? '').slice(0, 80)}${(userMsg ?? '').length > 80 ? '…' : ''}`,
      );

      if (!accessToken) {
        console.warn(`[EVE/run #${callId}] aborted — kein accessToken`);
        setError('Nicht eingeloggt');
        return null;
      }

      // Snapshot der page-eigenen History BEVOR wir die neue Frage pushen.
      const priorMessages = useEveChatStore.getState().byPage[pageKey] ?? [];
      const history: ChatHistoryTurn[] = priorMessages
        .filter((m) => !m.streaming && !m.error && m.text.length > 0)
        .map((m) => ({ role: m.role, text: m.text }));

      // Bild-Persistenz über Folgefragen: das zuletzt hochgeladene Chart-Bild
      // wird pro Page gemerkt. Lädt der User ein neues Bild hoch, merken + nutzen
      // wir es; stellt er eine Folgefrage OHNE neues Bild, hängen wir das gemerkte
      // Bild wieder an (innerhalb REUSE_WINDOW). So sieht EVE denselben Chart,
      // ohne dass der User ihn erneut hochladen muss.
      let effectiveImageData = imageData;
      let effectiveImageMime = imageMime;
      if (imageData) {
        rememberImage(pageKey, imageData, imageMime ?? 'image/png', priorMessages.length);
      } else {
        const recalled = recallImage(pageKey, priorMessages.length);
        if (recalled) {
          effectiveImageData = recalled.imageData;
          effectiveImageMime = recalled.imageMime;
        }
      }

      if (userMsg && userMsg.trim().length > 0) {
        // Chat-Bubble zeigt displayText (kurze Frage) wenn vorhanden, sonst
        // den vollen userMsg. So bleibt der Research-Wall aus dem Chat aber
        // EVE bekommt trotzdem alle Fakten/Quellen im userMsg.
        const bubbleText = displayText?.trim() || userMsg.trim();
        pushMessage(pageKey, createChatMessage({ role: 'user', text: bubbleText }));
      }

      pushMessage(
        pageKey,
        createChatMessage({
          role: 'assistant',
          text: '',
          agentKey,
          streaming: true,
        }),
      );

      setLoading(true);
      setError(null);

      try {
        const marketStats = deriveMarketStats(fullData);
        const tradesPromptBlock =
          trades.length === 0 ? null : `\n\nOFFENE_TRADES: ${trades.length} aktive Positionen.`;

        // Page-Context wird im Prompt vor dem AGENT_TASK eingehängt damit EVE
        // weiß auf welcher Page der User gerade ist und was er sieht.
        const pageContextBlock = formatPageContextBlock(pageContext);

        const ctx: RuntimeContext = {
          agentKey,
          userMsg,
          chatMode,
          marketStats,
          fullData,
          liveMetrics,
          activeAgents: eveProfile?.activeAgents ?? [],
          profile: eveProfile,
          entity: {
            entityState,
            emotionalContext,
            stressLevel,
            focusLevel,
            decisionConfidence,
          },
          portfolio,
          systemRisk,
          memories,
          rasRules,
          globalRasRules,
          macroHistory: macroEntries,
          user: {
            name: profile?.nickname ?? null,
            nickname: profile?.nickname ?? null,
          },
          isOwner,
          tradesPromptBlock,
          pageContextBlock,
          // Bei Bild-/Chart-Analyse den Gesamtmarkt-Snapshot weglassen — der Chart
          // ist die einzige Wahrheitsquelle, sonst mischt EVE irrelevantes Makro rein.
          // Gilt auch für Folgefragen mit wiederverwendetem Bild.
          suppressLiveData: noLiveData || Boolean(effectiveImageData),
        };

        const fullText = await runEveStreamed(
          {
            agentKey,
            userMsg,
            imageData: effectiveImageData,
            imageMime: effectiveImageMime,
            ctx,
            history,
            onChunk: (delta) => appendToLast(pageKey, delta),
            onCitation: (citation) => appendCitation(pageKey, citation),
          },
          { accessToken },
        );

        finishStreaming(pageKey);
        console.info(`[EVE/run #${callId}] ✓ ok | ${fullText.length}c`);
        return fullText;
      } catch (e) {
        // Insufficient-Credits: das Modal (eve:insufficient-credits) übernimmt die
        // Rückmeldung. Kein roher Fehler-Text in die Chat-Bubble — leere
        // Platzhalter-Bubble entfernen statt ⚠-Fehler zu zeigen.
        if (e instanceof InsufficientCreditsError) {
          console.info(`[EVE/run #${callId}] ⏸ insufficient credits — Modal übernimmt`);
          removeLastIfEmptyAssistant(pageKey);
          return null;
        }
        const msg = e instanceof Error ? e.message : 'EVE-Call fehlgeschlagen';
        console.error(`[EVE/run #${callId}] ✗ failed:`, msg, e);
        setError(msg);
        markError(pageKey, msg);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [
      location.pathname,
      accessToken,
      fullData,
      liveMetrics,
      macroEntries,
      memories,
      rasRules,
      globalRasRules,
      systemRisk,
      eveProfile,
      portfolio,
      trades,
      entityState,
      emotionalContext,
      stressLevel,
      focusLevel,
      decisionConfidence,
      profile,
      chatMode,
      pageContext,
      pushMessage,
      appendToLast,
      appendCitation,
      finishStreaming,
      markError,
      removeLastIfEmptyAssistant,
    ],
  );

  return { send, loading, error };
}
